/**
 * The library row, with the plan builder's drag gesture bound to it.
 *
 * ── Why the gesture is here and the row is not ───────────────────────────────────────────────
 * The row itself is `entities/exercise-library`'s since 17 September 2026, because the survey
 * editor's recording picker draws the same one. The DRAG could not go with it: its payload is
 * `libraryPick(item)` — a plan draft's `ExercisePick` — and an entity may not import another entity
 * (execution ADR §2). So the entity renders the `<li>` and this file supplies the ref it binds and
 * the flag it draws the dashed source treatment from.
 *
 * It is not a pass-through: delete it and 477 rows stop being draggable. `useDraggableItem` is a
 * hook, so a gesture per row needs a component per row — the list cannot call it in a loop.
 */

import { useRef } from 'react';

import { ExerciseLibraryRow, type ExerciseLibraryRowProps } from '~/entities/exercise-library';
import { useDraggableItem } from '~/shared/ui/dnd/useDraggableItem';

import { libraryPick } from '../model/librarySelection';

export interface RailDraggableRowProps {
  /** Everything the entity's list already resolved for this row. Forwarded whole. */
  readonly row: ExerciseLibraryRowProps;
  /** False where there is nowhere for a drop to land, which binds nothing at all. */
  readonly canDrag: boolean;
}

export function RailDraggableRow({ row, canDrag }: RailDraggableRowProps) {
  // The WHOLE row is the draggable. The payload carries the whole PICK rather than an id: the drop
  // lands in another slice entirely, and making it re-look-up the catalogue would couple two
  // features that do not import each other. A press with no movement is still a press, so the
  // insert path is untouched.
  const rowRef = useRef<HTMLLIElement>(null);
  const drag = useDraggableItem({
    ref: rowRef,
    // A drag carries THIS row and nothing else, even while it and four others are chosen: the drop
    // zone reads one payload and a multi-drag would need a preview, an indicator and a refusal
    // vocabulary none of which exist. The two acts stay separate — press to choose, drag to place.
    data: { kind: 'libraryExercise', pick: libraryPick(row.item) },
    canDrag,
  });

  return <ExerciseLibraryRow {...row} rowRef={rowRef} isDragging={drag.isDragging} />;
}
